import axios from "@/axios";
import router from "@/routes/router.js";
import Util from "@/util/util";

export const namespaced = true;

const defaultData = {
  id: null,
  name: null,
  email: null,
  roles: [],
  loginMethod: null,
};

export const state = {
  user: { ...defaultData }, 
  authenticatedUserId: null,
  status: null,
};

export const getters = {
  isLoggedIn: (state) =>
    !!state.user && !!state.user.name && !!state.user.email,
  authStatus: (state) => state.status,
  getUser: (state) => state.user,
  isPartnerAccount: (state) =>
    state.user && state.user.roles
      ? state.user.roles.indexOf(Util.PARTNER_NATION) > -1
      : false,
  isPM1Account: (state) =>
    state.user && state.user.roles
      ? state.user.roles.indexOf(Util.PM1) > -1
      : false,
  authUserId: (state) => state.authenticatedUserId,
  getLoginMethod: (state) => state.user.loginMethod,
  isLogistics: (state) =>
    !!state.user.roles && state.user.roles.indexOf("Logistics") > -1,
  isFinance: (state) =>
    !!state.user.roles && state.user.roles.indexOf("Finance") > -1,
  isSponsor: (state) =>
    !!state.user.roles && state.user.roles.indexOf("Sponsor") > -1,
  isTraining: (state) =>
    !!state.user.roles && state.user.roles.indexOf("Training") > -1,
  isSuperAdmin: (state) =>
    !!state.user.roles && state.user.roles.indexOf("AGATRS Team") > -1,
  isPartnerNation: (state)=>
    !!state.user.roles && state.user.roles.indexOf('PartnerNation')> -1, 
  isOUSD: (state) =>
    !!state.user.roles && state.user.roles.indexOf("Ousd") > -1,
  isViewOnly: (state) =>
    !!state.user.roles && state.user.roles.indexOf("ViewOnly") > -1,
  isPNManager: (state) =>
    !!state.user.roles && state.user.roles.indexOf("PartnerNationManager") > -1,
  isTier1: (state) =>
    !!state.user.roles && state.user.roles.indexOf("Tier1"), // Fixed typo indexof to indexOf
};

export const mutations = {
  AUTH_REQUEST(state) {
    state.status = "loading";
  },
  AUTH_LOGIN_SUCCESS(state) {
    state.status = "success";
  },
  AUTH_REGISTER_SUCCESS(state) {
    state.status = "Registered Successfully";
  },
  AUTH_ERROR(state) {
    state.status = "error";
  },
  AUTH_LOGOUT(state) {
    state.status = "logged out";
  },
  SET_USER(state, response) {
    state.user = response.data;
  },
  SET_AUTH_ID(state, response) {
    // make sure of that response.data.id exists before assigning
    state.authenticatedUserId = response && response.data ? response.data.id : null;
  },
  RESTORE_CONTEXT(state, response) {
    // make sure of that response.data exists before assigning
    if (response && response.data) {
      state.user.name = response.data.name;
      state.user.email = response.data.email;
      state.user.roles = response.data.roles;
      state.status = "success"; // Set status to success for restored sessions
    } else {
      // If restore fails or no data, clear the context
      state.user = { ...defaultData };
      state.authenticatedUserId = null;
      state.status = null;
    }
  },
  SET_LOGIN_METHOD(state, response) {
    state.loginMethod = response.data.loginMethod;
  },
  CLEAR_DATA(state) {
    state.user = { ...defaultData }; // Clear user data
    state.authenticatedUserId = null; // Clear authenticated user ID
    state.loginMethod = null; // Clear login method
    state.status = null; // Reset status
  },
};

// Add a module-level flag to prevent re-entrant/duplicate logout processing
let isProcessingLogout = false;

export const actions = {
  async register({ commit, dispatch }, formData) {
    return axios.post("/api/authentication/register", formData, {
      showToast: true,
      message: "You have successfully registered for AGATRS!",
    });
  },

  async registerPartnerNation({ commit, dispatch }, formData) {
    return axios.post("/api/authentication/register/parnternation", formData, {
      showToast: true,
      message: "You have successfully registered for AGATRS!",
    });
  },

  async login({ commit, dispatch }, credentials) {
    try {
      const response = await axios.post("/api/authentication/login", credentials, {
        showToast: true,
        showLoader: true,
        message: "You have successfully logged into AGATRS!",
      });
      commit("SET_USER", response);
      commit("AUTH_LOGIN_SUCCESS");

      try {
        await dispatch("restoreContext");
      } catch (_) {}

      const current = router.currentRoute.value || router.currentRoute;
      if (current && (current.name === "Login" || current.path === "/login")) {
        router.replace({ name: "Dashboard" }).catch(() => {});
      }
    } catch (error) {
      commit("AUTH_ERROR");
      throw error;
    }
  },

  // This is the new action for clearing all logout-related data and redirecting
  async clearAllLogoutData({ commit, state }) {
    if (isProcessingLogout) return;
    isProcessingLogout = true;

    try {
      if (state.user && state.user.name) {
        try {
          await axios.get("/api/authentication/logout");
        } catch (err) {
          const s = err && err.response ? err.response.status : null;
          if (s !== 301 && s !== 302 && s !== 401) {
            // Ignore network/redirect/unauthorized on logout; proceed to clear locally
          }
        }
      }
    } finally {
      commit("CLEAR_DATA");
      const current = router.currentRoute.value || router.currentRoute;
      if (!current || current.name !== "Login") {
        router.replace({ name: "Login", query: {}, params: { fromLogout: true } }).catch(() => {});
      }
      isProcessingLogout = false;
    }
  },
  async logout({ dispatch }) {
    //console.log("authentication/logout action called, dispatching clearAllLogoutData.");
    return dispatch("clearAllLogoutData");
  },
  async getUserByEmail({ commit }) {
    commit("SET_AUTH_ID", await axios.get("/api/applicationusers/email"));
  },

  async restoreContext({ commit }) {
    try {
      const response = await axios.get("/api/authentication/sessioncontext");
      commit("RESTORE_CONTEXT", response);
      if (response && response.data && response.data.name) {
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent("user-context-restored", {
            detail: { user: response.data },
          }));
        }, 100);
      }
    } catch (error) {
      if (error.response && (error.response.status === 401 || error.response.status === 403)) {
        commit("CLEAR_DATA");
      }
      throw error;
    }
  },

  async refreshSession({ commit, state }) {
    try {
      if (!state.user || !state.user.name) return false;
      const response = await axios.get("/api/authentication/sessioncontext");
      commit("RESTORE_CONTEXT", response);
      return !!(response && response.data && response.data.name);
    } catch (_) {
      // Do not clear auth on refresh failures; let UI handle via idle modal
      return false;
    }
  },
  async clearAuthenticatedUser({commit}){
    commit("SET_AUTH_ID", null);
  },

  async cac_login({ commit, dispatch, state }) {
    try {
      const response = await axios.get("/api/authentication/certificatelogin", {
        showToast: true,
        showLoader: true,
        message: "You have successfully logged into AGATRS!",
      });

      // Commit user data and success status
      commit("SET_USER", response);
      commit("AUTH_LOGIN_SUCCESS");

      const redirectTo = { name: 'Dashboard' };
      const currentRoute = router.currentRoute.value || router.currentRoute;
      
      // Navigate to Dashboard if the the current page is Login.
      if (currentRoute.name === 'Login' || currentRoute.path === '/login') {
        setTimeout(() => {
          router.push(redirectTo).catch(err => {
            if (err.name !== 'NavigationDuplicated' && err.name !== 'NavigationCancelled') {
              console.error('CAC Login navigation error:', err);
            }
          });
        }, 100); // Short delay
      }

    } catch (error) {
      commit("AUTH_ERROR");
      console.error("CAC Login action failed:", error);
      throw error;
    }
  },
  async cac_register({ commit }, user) {
    return axios.get("/api/authentication/certificateregister", {
      showToast: true,
      message: "You have sucsessfully registered your certificate!",
    });
  },
  // This existing clearData action can be consolidated into clearAllLogoutData
  // For now, keeping it here but clearAllLogoutData is more comprehensive for logout scenarios.
  async clearData({ commit }) {
    commit("CLEAR_DATA");
  },
};